import 'core-js';

import './app.scss';
import html from './app.html';
import {partial} from './utils/partial';
import Mustache from 'mustache';

// Utils
import {Router} from './utils/router';
import {state} from './utils/state';
import {emitter, EVENTS} from './utils/emitter';
import {prerender, supportedLanguages} from './configs/global';
import {sleep} from './utils/helpers';

// Views
import {Intro} from './views/pages/intro/intro';
import {Home} from './views/pages/home/home';
import {About} from './views/pages/about/about';
import {Story} from './views/pages/story/story';
import {Loader} from './views/pages/loader/loader';

// Components
import {Flag} from './views/components/flag/flag';
import {Header} from './views/components/header/header';
import {Footer} from './views/components/footer/footer';
import {LandscapeOverlay} from './views/components/overlay/overlay';

// Controllers: Emit events
import {Window} from './utils/controllers/window';
import {Mouse} from './utils/controllers/mouse';
import {Wheel} from './utils/controllers/wheel';

/**
 * Kicks off the applications and init views.
 * This is created when the document is ready.
 */
class App {
  private loader: Loader;
  private flag: Flag;
  private home: Home;
  private header: Header;
  private footer: Footer;

  constructor(container: HTMLElement) {
    container.appendChild(partial(html, '#app'));

    // Allow tags in HTML Mustache strings
    Mustache.escape = text => text;

    // Required in rest of the app
    this.initLanguage();
    this.initControllers();

    // Init and kick off loader
    this.loader = new Loader();
    emitter.on(EVENTS.loaded, this.onPreloaded);
    this.loader.start();
    this.loader.animateIn();
    this.show();
  }

  private init() {
    this.flag = new Flag();
    this.footer = new Footer();
    this.header = new Header(this.footer);
    this.home = new Home(this.header, this.footer);

    new LandscapeOverlay();

    this.addHandlers();
    this.initRouter();
  }

  private onPreloaded = async () => {
    this.loader.animateOut().then(() => {
      this.loader.kill();
      this.loader = null;
    });
    await sleep(0.15);
    this.init();
  };

  // -- Init Methods

  private initLanguage() {
    const setLA = local => (window.app.classList.add(local), ((window as any).LANGUAGE = local));

    // 1) Look at Params
    {
      const url = new URL(location.href);
      const local = url.searchParams.get('lang');
      if (local && supportedLanguages.includes(local)) return setLA(local);
    }

    // 2) Is there a saved option?
    {
      const local = localStorage.getItem('lang');
      if (local && supportedLanguages.includes(local)) return setLA(local);
    }

    // 3) Detect using browser API
    {
      const local = navigator.languages ? navigator.languages.filter(x => supportedLanguages.find(y => x == y))[0] : 'en';

      if (local) return setLA(local);
    }

    // 4) Fallback to english
    setLA('en');
  }

  private initRouter() {
    Router.on('/', () =>
      state.setValue('view', {
        name: 'home',
        instance: this.home
      })
    )
      .on('/about', () =>
        state.setValue('view', {
          name: 'about',
          instance: new About(this.flag, this.header, this.footer)
        })
      )
      .on('/:story', params => {
        this.home.setStory(new Story(this.home, params));
        state.setValue('view', {
          name: 'story',
          instance: this.home
        });
      });

    Router.listen();
  }

  private show() {
    if (prerender) return;
    document.body.style.opacity = '1';
  }

  private initControllers() {
    new Window();
    new Mouse();
    new Wheel();
  }

  private addHandlers() {
    state.on(EVENTS.stateChange, this.onRouterChange);
  }

  // -- Event Handlers

  /**
   * Gives you the new incoming view as param.
   * Use state store to access the previews view.
   * Call animate in and out here.
   */
  private onRouterChange = async ({property, value, previous}) => {
    if (property !== 'view') return;
    if (value === previous.name) return;
    state.setValue('first', !previous.instance);

    this.header.updateState();

    // If previousView is null and it's the home page
    // means that we should animanimatate in from the intro.
    if (state.getValue('first') && value.name == 'home') {
      const skipIntro = new URL(location.href).searchParams.get('skip')!!;

      if (skipIntro) {
        this.header.animateIn();
        this.footer.animateIn();
        this.home.animateIn();
        return;
      }

      const intro = new Intro(this.flag, this.header, this.footer);
      await Promise.resolve();
      await intro.animateIn();
      intro.kill();
      this.home.animateIn();
      return;
    }

    // Transition from a Story to Home
    if (value.name == 'home' && previous.name == 'story') {
      this.home.removeStory();
      return;
    }

    // Story related transition
    if (value.name == 'story') {
      // Transition from Home to a Story
      // 'setStory' already called in the router, so return
      if (previous.name == 'story' || previous.name == 'home') {
        this.home.animateInStory();
        return;
      }

      if (previous.name === 'about') {
        await previous.instance.animateOut();
        previous.instance.kill();
      }

      this.home.animateInStory();
      return;
    }

    // Default Transition
    if (previous.instance) {
      await previous.instance.animateOut();
      previous.instance.kill();
    }

    value.instance.animateIn();
  };
}

// Lift off!
window.onload = () => {
  document.body.innerHTML = ``;
  new App(document.body);
};
